import { useMemo } from "react";
import type { DashboardCardType } from "../../../../types/dashboardLayout";

const MINI_STATS_NEEDING_STATISTICS: DashboardCardType[] = [
  "closedTrades",
  "activeTrades",
  "largestWin",
  "averageWin",
  "largestLoss",
  "averageLoss",
];

const MINI_STATS_NEEDING_PREVIOUS_PERIOD: DashboardCardType[] = [
  "closedTrades",
  "activeTrades",
  "largestWin",
  "averageWin",
  "largestLoss",
  "averageLoss",
];

const CARDS_NEEDING_DASHBOARD_SUMMARY: DashboardCardType[] = [
  "portfolio",
  "expectancy",
  "winRate",
  "monthlyPnL",
  "roi",
  "portfolioGrowth",
  "winRateTrend",
  "pnlMilestones",
];

export type DashboardFetchPlan = {
  ready: boolean;
  needsStatistics: boolean;
  needsPreviousPeriodStatistics: boolean;
  needsSymbolStatistics: boolean;
  needsDailyActivity: boolean;
  needsDashboardSummary: boolean;
};

export type DashboardFetchPlanOptions = {
  /** When false, callers should disable fetches (layout hydrating or no account). */
  ready?: boolean;
};

function visibleHasAny(
  visible: Set<DashboardCardType>,
  cardTypes: readonly DashboardCardType[]
): boolean {
  return cardTypes.some((cardType) => visible.has(cardType));
}

/**
 * Layout intent for which endpoints the dashboard needs.
 * `ready` is separate — do not fold it into needs* (that wiped data mid-hydrate).
 * Callers should use `enabled: plan.ready && plan.needsX` (summary may use analyticsReady && needsX).
 */
export function getDashboardFetchPlan(
  visible: Set<DashboardCardType>,
  options?: DashboardFetchPlanOptions
): DashboardFetchPlan {
  const ready = options?.ready !== false;

  const needsStatistics =
    visibleHasAny(visible, MINI_STATS_NEEDING_STATISTICS) ||
    visible.has("performanceBreakdown") ||
    visible.has("streakAnalysis") ||
    visible.has("pnlOverTime") ||
    visible.has("longVsShortPerformance");

  const needsPreviousPeriodStatistics = visibleHasAny(
    visible,
    MINI_STATS_NEEDING_PREVIOUS_PERIOD
  );

  const needsDashboardSummary = visibleHasAny(visible, CARDS_NEEDING_DASHBOARD_SUMMARY);

  return {
    ready,
    needsStatistics,
    needsPreviousPeriodStatistics,
    needsSymbolStatistics: visible.has("performanceBreakdown"),
    needsDailyActivity: visible.has("tradeActivityCalendar"),
    needsDashboardSummary,
  };
}

export function useDashboardFetchPlan(
  visible: Set<DashboardCardType>,
  options?: DashboardFetchPlanOptions
): DashboardFetchPlan {
  return useMemo(
    () => getDashboardFetchPlan(visible, options),
    [visible, options?.ready]
  );
}
